import { useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { FiCheck } from "react-icons/fi";
import { getNotifications, markNotificationRead, markAllNotificationsRead } from "../../services/notificationApi";
import useAsync from "../../hooks/useAsync";
import { Card, PageHeader, Button, EmptyState, Loader, ErrorState } from "../../components/common";
import { formatDateTime } from "../../utils/format";

const api = { getNotifications, markNotificationRead, markAllNotificationsRead };

const PAGE = 50;

export default function NotificationsPage() {
  const navigate = useNavigate();
  const { refreshCounters } = useOutletContext() || {};
  const [limit, setLimit] = useState(PAGE);
  const { data, error, reload } = useAsync(() => api.getNotifications({ limit }), [limit]);

  if (error) return <ErrorState message={error} onRetry={reload} />;
  if (!data) return <Loader />;

  const unread = data.filter((n) => !n.read).length;

  const open = async (n) => {
    if (!n.read) await api.markNotificationRead(n.id);
    refreshCounters?.();
    if (n.link) navigate(n.link);
    else reload();
  };

  const markAll = async () => {
    await api.markAllNotificationsRead();
    refreshCounters?.();
    reload();
  };

  return (
    <div className="page page-narrow">
      <PageHeader
        title="Notifications"
        subtitle={unread ? `${unread} unread` : "You're all caught up."}
        actions={unread > 0 && <Button variant="secondary" icon={FiCheck} onClick={markAll}>Mark all as read</Button>}
      />
      <Card padded={false}>
        {data.length === 0 ? (
          <EmptyState title="No notifications" text="Approvals, reminders and results will show up here." />
        ) : (
          <ul className="notif-page-list">
            {data.map((n) => (
              <li key={n.id}>
                <button type="button" className={`notif-page-item ${n.read ? "" : "is-unread"}`} onClick={() => open(n)}>
                  <span className="notif-dot" aria-hidden="true" />
                  <span className="notif-body">
                    <strong>{n.title}</strong>
                    <span>{n.message}</span>
                  </span>
                  <time>{formatDateTime(n.at)}</time>
                </button>
              </li>
            ))}
          </ul>
        )}
      </Card>
      {data.length >= limit && limit < 200 && (
        <div className="row-actions">
          <Button variant="secondary" onClick={() => setLimit((l) => Math.min(200, l + PAGE))}>Show older notifications</Button>
        </div>
      )}
    </div>
  );
}
